import { useEffect, useState } from "react";
import Buttons from "../../../../components/UI/Buttons";
import FieldLoader from "../../../../components/UI/FieldLoader";
import FormInputs from "../../../../components/UI/FormInputs";
import EmrConfigService from "../../../../services/EmrConfigService";
import EmrVisitsService from "../../../../services/EmrVisitsService";
import { OK } from "../../../../utils/constant";
import { isSet } from "../../../../utils/commonUtils";
import PERMISSIONS from "../../../../utils/AclPermissions";
import Alert from "../../../../components/UI/ConfirmBox/Alert";

const NewEditChartNote = (props) => {
  const { visit_id, Toast, Loader, visitDetails } = props;
  const [notesByScreen, setNotesByScreen] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [pendingPageSwitch, setPendingPageSwitch] = useState(null);

  const visitRecord = visitDetails?.["0"] ?? visitDetails;
  const providerDetails = visitDetails?.primaryProvider || visitDetails?.["0"]?.primaryProvider || visitDetails?.primary_provider || visitDetails?.["0"]?.primary_provider;
  const providerDisplayName = providerDetails
    ? `${providerDetails.first_name || ''} ${providerDetails.last_name || ''}`.trim()
    : "-";
  const hasAttestation = !!visitRecord?.history_review_attestation;

  const options = [
    { label: "HPI", value: "HPI" , parent: "History" },
    { label: "PMH", value: "PMH" , parent: "History" },
    { label: "ROS", value: "ROS" , parent: "History" },
    { label: "History Recap", value: "HISTORY_RECAP", parent: "Examination" },
    { label: "Focused Injury", value: "PROBLEM_FOCUSED" , parent: "Examination" },
    { label: "Musculoskeletal", value: "MUSCULOSKELETAL" , parent: "Examination" },
    { label: "Organ Systems", value: "ORGAN_SYSTEMS" , parent: "Examination" },
    { label: "ROM / Testing", value: "ROM_TESTING" , parent: "Examination" },
    { label: "Lacerations", value: "EXAMINATION_LACERATIONS" , parent: "Examination" },
    { label: "Orders/Results", value: "ALL" , parent: "Order/Results" },
    { label: "X-ray", value: "X_RAY" , parent: "Order/Results" },
    { label: "Labs", value: "LAB" , parent: "Order/Results" },
    { label: "Diagnostics", value: "DIAGNOSTIC" , parent: "Order/Results" },
    { label: "Treatments", value: "TREATMENTS" , parent: "Order/Results" },
    { label: "Inject / Vaccines", value: "INJECT_VACCINES" , parent: "Order/Results" },
    { label: "Dispensed RX", value: "PRESCRIPTIONS" , parent: "Order/Results" },
    { label: "DMEs", value: "DMES" , parent: "Order/Results" },
    { label: "Referrals", value: "REFERRALS" , parent: "Order/Results" },
    { label: "Assessment", value: "ASSESSMENT" , parent: "Assessment" },
    { label: "MDM / Plan", value: "MDM_PLAN" , parent: "MDM / Plan" },
    { label: "Coding / Billing", value: "CODING_BILLING" , parent: "MDM / Plan" },
    { label: "Lacerations", value: "PROCEDURES_LACERATIONS" , parent: "Procedures" },
    { label: "Other Procedures", value: "OTHER_PROCEDURES" , parent: "Procedures" },
    { label: "Work Restrictions", value: "WORK_RESTRICTIONS" },
    { label: "Return/FollowUp", value: "RETURN_FOLLOWUP" },
    { label: "School Note", value: "SCHOOL_NOTE" },
    { label: "Chart by Superbill", value: "CHART_BY_SUPERBILL" },
    { label: "Chart by Dictation", value: "CHART_BY_DICTATION" },
  ];

  useEffect(() => {
    if (visit_id) {
      fetchAllNotes();
    }
  }, [visit_id,hasAttestation]);

  useEffect(() => {
    // Set first page with notes as selected when notes are loaded
    if (options.length > 0 && !selectedPage && Object.keys(notesByScreen).length > 0) {
      // Find first page that has notes (non-empty text)
      const firstPageWithNotes = options.find(
        (opt) => notesByScreen[opt.value]?.text && notesByScreen[opt.value].text.trim() !== ""
      );
      if (firstPageWithNotes) {
        setSelectedPage(firstPageWithNotes.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesByScreen]);

  const getHistoryRecapContent = () => {
    if (!hasAttestation) return "";
    
    const attestationDate = visitRecord?.history_review_attestation_date 
      ? new Date(visitRecord.history_review_attestation_date).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      : '-';
    
    return `<div style="margin-bottom: 15px; color: #212529;">
      <strong style="font-weight: bold; color: #212529;">Provider Attestation:</strong> <span style="color: #212529;">I reviewed the patient's history prior to today's evaluation and management and updated it as appropriate.</span><br/>
      <strong style="font-weight: bold; color: #212529;">Reviewed by:</strong> <span style="color: #212529;">${providerDisplayName}</span> - <strong style="font-weight: bold; color: #212529;">Date:</strong> <span style="color: #212529;">${attestationDate}</span>
    </div>`;
  };

  const fetchAllNotes = async () => {
    try {
      Loader(true);
      setLoading(true);
      // "ALL" is an aggregated view computed below from individual order categories.
      const activeOptions = options.filter((opt) => opt.value && opt.value !== 'HISTORY_RECAP' && opt.value !== 'ALL');

      const [responses, ordersResponse] = await Promise.all([
        Promise.all(
          activeOptions.map(async (opt) => {
            try {
              const response = await EmrConfigService.visifetchProgressData(
                visit_id,
                opt.value
              );

              if (!response || response.code !== OK) {
                return { key: opt.value, text: "" };
              }

              const data = response.data || {};
              const display_value = data.display_value ?? null;

              return {
                key: opt.value,
                text: isSet(display_value, ""),
              };
            } catch (err) {
              console.error(
                `Error fetching progress data for ${opt.value}:`,
                err
              );
              return { key: opt.value, text: "" };
            }
          })
        ),
        EmrVisitsService.ListOrders(visit_id),
      ]);

      const notesMap = {};
      responses.forEach(({ key, text }) => {
        notesMap[key] = {
          text,
          originalText: text, // Store original text for comparison
          dirty: false,
        };
      });

      // Compute combined Orders/Results view (ALL) from the orders/results API.
      // This includes both manually placed orders and AI-suggested orders (accepted into DB).
      const orderCategoryToLabel = {
        X_RAY: "X-ray",
        LAB: "Labs",
        DIAGNOSTIC: "Diagnostics",
        TREATMENTS: "Treatments",
        INJECT_VACCINES: "Inject / Vaccines",
        PRESCRIPTIONS: "Dispensed RX",
        DMES: "DMEs",
        REFERRALS: "Referrals",
      };

      const orderCategoryOrder = [
        "X_RAY",
        "LAB",
        "DIAGNOSTIC",
        "TREATMENTS",
        "INJECT_VACCINES",
        "PRESCRIPTIONS",
        "DMES",
        "REFERRALS",
      ];

      const orders = ordersResponse?.orders || [];
      const combinedParts = [];
      const escapeHtml = (value) =>
        String(value ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const renderOrderJustification = (order) => {
        const payload = order?.justification_json;
        if (!payload) return "";

        let data = payload;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = { rationale: payload };
          }
        }
        if (!data || typeof data !== "object") return "";

        const lines = [];
        const sections = data?.rationale_sections;
        if (sections && typeof sections === "object" && !Array.isArray(sections)) {
          Object.entries(sections).forEach(([label, value]) => {
            const l = String(label || "").trim();
            const v = String(value || "").trim();
            if (l && v) {
              lines.push(`<div><strong>${escapeHtml(l)}:</strong> ${escapeHtml(v)}</div>`);
            }
          });
        }

        if (lines.length === 0 && data?.rationale) {
          const txt = String(data.rationale || "").trim();
          if (txt) lines.push(`<div><strong>Justification:</strong> ${escapeHtml(txt)}</div>`);
        }

        return lines.length
          ? `<div style="margin-top:4px;color:#343a40;font-size:12px; line-height: 1.35;">${lines.join("")}</div>`
          : "";
      };

      const getDisplayName = (order) => {
        const isMedication =
          order?.activity_type === "Medication - Dispensed" ||
          order?.activity_type === "Medication - Injectable";
        if (isMedication && order?.emr_field_label) return order.emr_field_label;
        return order?.activity_name || "-";
      };

      const formatStatusLabel = (status) => {
        if (!status) return "-";
        const s = String(status).trim();
        if (!s || s === "-") return "-";

        const titleCase = (str) =>
          str
            .replace(/_/g, " ")
            .split(" ")
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ");

        // Handle statuses like "Completed/Administrated"
        return s
          .split("/")
          .map((part) => titleCase(part.trim()))
          .join(" / ");
      };

      orderCategoryOrder.forEach((category) => {
        const categoryOrders = orders.filter((o) => (o?.category || "") === category);
        if (!categoryOrders.length) return;

        const categoryParts = [];
        combinedParts.push(
          `<div style="margin-bottom:8px;"><strong>${orderCategoryToLabel[category] || category}</strong></div>`
        );

        categoryOrders.forEach((order) => {
          const displayName = getDisplayName(order);
          const result = formatStatusLabel(order?.status || "-");
          const activityStatus = formatStatusLabel(order?.activity_status || order?.status || "-");
          const justificationHtml = renderOrderJustification(order);
          categoryParts.push(
            `<div style="margin-left:12px;margin-bottom:4px;">
              • <strong>${displayName}</strong>
              <div style="margin-left:14px;color:#495057;font-size:12px; line-height: 1.35;">
                <div>Result: ${result}</div>
                <div>Activity Status: ${activityStatus}</div>
                ${justificationHtml}
              </div>
            </div>`
          );
        });

        combinedParts.push(categoryParts.join("<br/>"));
      });

      // If any orders have categories not covered above, show them at the end.
      const uncoveredOrders = orders.filter((o) => !orderCategoryOrder.includes(o?.category));
      if (uncoveredOrders.length) {
        combinedParts.push(
          `<div style="margin-bottom:8px;"><strong>Other</strong></div>`
        );
        uncoveredOrders.forEach((order) => {
          const displayName = getDisplayName(order);
          const result = formatStatusLabel(order?.status || "-");
          const activityStatus = formatStatusLabel(order?.activity_status || order?.status || "-");
          const justificationHtml = renderOrderJustification(order);
          combinedParts.push(
            `<div style="margin-left:12px;margin-bottom:4px;">
              • <strong>${displayName}</strong>
              <div style="margin-left:14px;color:#495057;font-size:12px; line-height: 1.35;">
                <div>Result: ${result}</div>
                <div>Activity Status: ${activityStatus}</div>
                ${justificationHtml}
              </div>
            </div>`
          );
        });
      }

      const combinedText = combinedParts.join("<br/>");
      notesMap["ALL"] = {
        text: combinedText || "",
        originalText: combinedText || "",
        dirty: false,
      };

      const historyRecapContent = getHistoryRecapContent();
      if (historyRecapContent) {
        notesMap['HISTORY_RECAP'] = {
          text: historyRecapContent,
          originalText: historyRecapContent,
          dirty: false,
        };
      }

      setNotesByScreen(notesMap);
    } catch (err) {
      console.error("Error fetching all progress data:", err);
    } finally {
      setLoading(false);
      Loader(false);
    }
  };

  // Helper function to normalize text for comparison (strip HTML tags and normalize whitespace)
  const normalizeText = (text) => {
    if (!text) return "";
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const handleTextareaChange = (screenType, val) => {
    setNotesByScreen((prev) => {
      const current = prev[screenType] || { text: "", originalText: "" };
      const originalText = current.originalText || current.text || "";
      // Normalize both values for comparison to handle HTML formatting differences
      const normalizedVal = normalizeText(val).trim();
      const normalizedOriginal = normalizeText(originalText).trim();
      const hasChanged = normalizedVal !== normalizedOriginal;
      
      return {
        ...prev,
        [screenType]: {
          ...current,
          text: val,
          dirty: hasChanged,
        },
      };
    });
  };

  const updateProgress = async (screenType, data) => {
    try {
      const payload = {
        displayText: data,
      };
      await EmrConfigService.upateProcessData(visit_id, screenType, payload);

      if (!data) {
        Toast.success("Progress Notes Cleared");
      } else {
        Toast.success("Progress Notes Updated");
      }
    } catch (error) {
      console.error("Failed to update progress data :", error);
      throw error;
    }
  };

  const handleSave = async (screenType, isClear = false) => {
    try {
      const current = notesByScreen[screenType] || { text: "" };
      const rawValue = isClear ? "" : current.text;

      setLoading(true);
      await updateProgress(screenType, rawValue);

      setNotesByScreen((prev) => {
        const updated = {
          ...prev,
          [screenType]: {
            ...(prev[screenType] || {}),
            text: rawValue,
            originalText: rawValue, // Update original text after save
            dirty: false,
          },
        };

        // If the current page was cleared and it was selected, switch to first available page with notes
        if (isClear && selectedPage === screenType) {
          const firstPageWithNotes = options.find(
            (opt) => {
              const note = updated[opt.value];
              return opt.value !== screenType && note && note.text && note.text.trim() !== "";
            }
          );
          if (firstPageWithNotes) {
            setSelectedPage(firstPageWithNotes.value);
          } else {
            setSelectedPage(null);
          }
        }

        return updated;
      });
    } catch (err) {
      console.error("Error saving progress data:", err);
      Toast.error("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (pageValue) => {
    if (!selectedPage) {
      setSelectedPage(pageValue);
      return;
    }

    const currentNote = notesByScreen[selectedPage];
    if (!currentNote) {
      setSelectedPage(pageValue);
      return;
    }

    // Only show alert if there are actual unsaved changes (text differs from original)
    // Normalize both texts to handle HTML formatting differences
    // const originalText = normalizeText(currentNote.originalText || "").trim();
    // const currentText = normalizeText(currentNote.text || "").trim();
    // const hasUnsavedChanges = currentText !== originalText;

    // Only show alert if there are unsaved changes
    // if (hasUnsavedChanges) {
    //   setPendingPageSwitch(pageValue);
    //   setShowUnsavedAlert(true);
    // } else {
    //   // No unsaved changes, switch directly
    //   setSelectedPage(pageValue);
    // }
    
    // Allow direct switching without alert
    setSelectedPage(pageValue);
  };

  const handleProceed = () => {
    // Discard changes and revert to original text
    if (selectedPage) {
      const currentNote = notesByScreen[selectedPage];
      if (currentNote) {
        const originalText = currentNote.originalText || "";
        setNotesByScreen((prev) => ({
          ...prev,
          [selectedPage]: {
            ...prev[selectedPage],
            text: originalText, // Revert to original text
            dirty: false,
          },
        }));
      }
    }
    setShowUnsavedAlert(false);
    if (pendingPageSwitch) {
      setSelectedPage(pendingPageSwitch);
      setPendingPageSwitch(null);
    }
  };

  const handleCancel = () => {
    setShowUnsavedAlert(false);
    setPendingPageSwitch(null);
  };

  const handleSaveAndSwitch = async () => {
    if (!selectedPage) {
      handleCancel();
      return;
    }

    try {
      const current = notesByScreen[selectedPage] || { text: "" };
      setLoading(true);
      await updateProgress(selectedPage, current.text);

      setNotesByScreen((prev) => {
        const current = prev[selectedPage] || { text: "" };
        return {
          ...prev,
          [selectedPage]: {
            ...prev[selectedPage],
            text: current.text,
            originalText: current.text, // Update original text after save
            dirty: false,
          },
        };
      });

      setShowUnsavedAlert(false);
      if (pendingPageSwitch) {
        setSelectedPage(pendingPageSwitch);
        setPendingPageSwitch(null);
      }
    } catch (err) {
      console.error("Error saving before switch:", err);
      Toast.error("Failed to save data");
    } finally {
      setLoading(false);
    }
  };

  const currentNote = selectedPage ? notesByScreen[selectedPage] : null;
  const currentOption = options.find((opt) => opt.value === selectedPage);

  return (
    <div className="sh_cardBody py-0" style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: "calc(100vh - 150px)" }}>
      <div className="position-relative" style={{ height: "700px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <FieldLoader isLoading={isLoading} />
        
        <div className="d-flex" style={{ flex: 1, overflow: "hidden", minHeight: 0, height: "100%" }}>
          {/* Left Sidebar - Pages List */}
          <div
            className="border-end w-25"
            style={{
              width: "220px",
              backgroundColor: "#f8f9fa",
              padding: "0",
              display: "flex",
              flexDirection: "column",
              height: "calc(90vh - 250px)",
              maxHeight: "100%", 
              overflow: "hidden",
              marginRight: "10px",
            }}
          >
            <div className="px-3 py-3 border-bottom" style={{ backgroundColor: "#dee2e6", position: "sticky", top: 0, zIndex: 1, flexShrink: 0 }}>
              <h6 className="text-sm font-weight-bold mb-0" style={{ color: "#495057" }}>Section based progress notes</h6>
            </div>
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {options
                .filter((opt) => {
                  // Only show pages that have notes (non-empty text)
                  const note = notesByScreen[opt.value];
                  return note && note.text && note.text.trim() !== "";
                })
                .map((opt) => {
                  const note = notesByScreen[opt.value] || { text: "", originalText: "", dirty: false };
                  const isActive = selectedPage === opt.value;
                  // Check if text has actually changed from original
                  const originalText = note.originalText || "";
                  const currentText = note.text || "";
                  const hasChanges = currentText !== originalText;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => handlePageClick(opt.value)}
                      style={{
                        padding: "8px 15px",
                        cursor: "pointer",
                        backgroundImage: isActive ? "linear-gradient(270deg, #9AA1FF 6.25%, #4474E6 100%)" : "none",
                        backgroundColor: "transparent",
                        color: isActive ? "#000" : "#495057",
                        borderLeft: isActive ? "4px solid #0056b3" : "4px solid transparent",
                        transition: "all 0.2s ease",
                        position: "relative",
                        borderBottom: "1px solid #e9ecef",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "#e9ecef";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          style={{
                            fontWeight: isActive ? "600" : "500",
                            fontSize: "14px",
                            flex: 1,
                          }}
                        >
                          {opt.label}
                        </span>
                        {/* {hasChanges && (
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              backgroundColor: "#ffc107",
                              display: "inline-block",
                              marginLeft: "8px",
                              boxShadow: "0 0 0 2px rgba(255, 193, 7, 0.3)",
                            }}
                            title="Unsaved changes"
                          />
                        )} */}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Right Side - Editor */}
          <div
            className="flex-grow-1 w-75"
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {selectedPage && currentOption ? (
              <>
                <div 
                  className="border-bottom"
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#f8f9fa",
                    flexShrink: 0,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="text-sm font-weight-bold mb-0" style={{ color: "#212529" }}>
                        {currentOption.label}
                      </h5>
                      <small className="text-muted" style={{ fontSize: "12px" }}>Page: {currentOption.parent}</small>
                    </div>
                    <div className="d-flex" style={{ gap: "8px" }}>
                      {selectedPage !== "HISTORY_RECAP" && selectedPage !== "ALL" && (
                        <Buttons
                          className="btn btn-primary btn-sm"
                          acl={
                            currentNote?.dirty
                              ? PERMISSIONS.ANY_PERMISSION
                              : PERMISSIONS.NO_PERMISSION
                          }
                          clickHandler={() => handleSave(selectedPage)}
                          label={currentNote?.dirty ? `Save ${currentOption.label}` : `Saved ${currentOption.label}`}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div 
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    padding: "5px",
                    minHeight: 0,
                  }}
                >
                  <div 
                    className="sh_cardBox"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      minHeight: 0,
                    }}
                  >
                    <div 
                      className="sh_cardBody"
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minHeight: 0,
                        padding: "5px",
                      }}
                    >
                      <div style={{ flex: 1, overflow: "hidden", minHeight: 0, display: "flex", flexDirection: "column", height: "100%" }}>
                        <FormInputs
                          type="Editor"
                          value={currentNote?.text || ""}
                          height="calc(100vh - 350px)"
                          formats={{
                            formats: ["background"],
                          }}
                          modules={{
                            toolbar: [
                              ["bold"],
                              [{ background: ["yellow", "red", "green", "blue"] }],
                              ["clean"],
                            ],
                          }}
                          readOnly={selectedPage === "HISTORY_RECAP" || selectedPage === "ALL"}
                          changeHandler={(value) =>
                            handleTextareaChange(selectedPage, value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div 
                className="d-flex align-items-center justify-content-center"
                style={{ flex: 1 }}
              >
                <p className="text-muted">Select a page from the left to view/edit progress notes</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Alert - Commented out */}
      {/* {showUnsavedAlert && (
        <Alert
          contentTitle="You have unsaved changes. Are you sure you want to navigate away? Changes will be lost."
          content="You have unsaved changes. Are you sure you want to navigate away? Changes will be lost."
          yesButtonName="Discard"
          noButtonName="Cancel"
          type="warning"
          onDelete={handleProceed}
          onClose={handleCancel}
          confirmButtonClassName="btn btn-danger"
          cancelButtonClassName="btn btn-secondary"
          saveAndClose={true}
          onSaveAndClose={handleSaveAndSwitch}
        />
      )} */}
    </div>
  );
};

export default NewEditChartNote;
